import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getError } from '../../utils';
import { Store } from '../../Store';
import reservaService from '../../services/reserva.service';
import compradorService from '../../services/comprador.service';
import ticketService from '../../services/ticket.service';
import CheckoutStepper from '../../components/checkout/CheckoutStepper';
import AccountChoice from '../../components/checkout/AccountChoice';
import CuentaDestinoField, {
  useCuentasDestino,
} from '../../components/checkout/CuentaDestinoField';
import { paths } from '../../routes/paths';
import { getAnalyticsAttribution, trackAnalyticsEvent } from '../../lib/analytics';
import {
  isSharedBoard,
  mostrarNumerosOf,
  pickerModeOf,
  toTicketsPerSquare,
} from '../../lib/boardPlans';

const reducer = (state, action) => {
  switch (action.type) {
    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return { ...state, loadingCreate: false };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };
    default:
      return state;
  }
};

function Field({ label, error, children }) {
  return (
    <div className='space-y-1'>
      <label className='block text-sm font-medium text-ink'>{label}</label>
      {children}
      {error && <p className='text-xs text-red-500'>{error}</p>}
    </div>
  );
}

function InputField({ ...props }) {
  return (
    <input
      className='w-full rounded-xl border border-divider bg-white px-3 py-2.5 text-sm text-ink placeholder:text-zinc-400 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/20'
      {...props}
    />
  );
}

export default function NuevaReservaCasilla() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  /* ── Restore casillas: location.state first, then sessionStorage ── */
  const [locationState] = useState(() => {
    if (location.state?.casillas?.length) return location.state;
    try {
      const raw = sessionStorage.getItem('pendingCheckout');
      if (raw) {
        const pending = JSON.parse(raw);
        if (
          (pending.returnTo === paths.reserveBoard || pending.returnTo === '/reservarCasillas') &&
          pending.casillas?.length
        ) {
          sessionStorage.removeItem('pendingCheckout');
          return {
            casillas: pending.casillas,
            rifaInfo: pending.rifaInfo,
            totalPrice: pending.totalPrice,
            totalTickets: pending.totalTickets,
            planByCasilla: pending.planByCasilla,
          };
        }
      }
    } catch {
      /* ignore */
    }
    return location.state || {};
  });

  const { casillas = [], rifaInfo } = locationState;

  /* ── Shared board: the buyer picks a plan for each square (default: first plan) ── */
  const shared = isSharedBoard(rifaInfo);
  const plans = useMemo(
    () =>
      (rifaInfo?.config?.categories || []).map(p => ({
        ...p,
        precioCasilla: Number(p.precioCasilla) || 0,
        ticketsPorCasilla: toTicketsPerSquare(p.ticketsPorCasilla),
      })),
    [rifaInfo]
  );
  const [planByCasilla, setPlanByCasilla] = useState(() => {
    const saved = locationState.planByCasilla || {};
    const first = rifaInfo?.config?.categories?.[0]?.nombre;
    return Object.fromEntries(casillas.map(c => [c._id, saved[c._id] || first]));
  });
  /** Price / numbers / label per selected square (server recomputes everything). */
  const items = casillas.map(c => {
    if (!shared) {
      return {
        casilla: c,
        categoria: c.categoria,
        color: c.color,
        precio: Number(c.precioCasilla) || 0,
        tickets: toTicketsPerSquare(c.ticketsPorCasilla),
      };
    }
    const plan = plans.find(p => p.nombre === planByCasilla[c._id]) || plans[0] || {};
    return {
      casilla: c,
      categoria: plan.nombre,
      color: plan.color,
      precio: plan.precioCasilla || 0,
      tickets: plan.ticketsPorCasilla || 0,
    };
  });
  const totalPrice = items.reduce((sum, i) => sum + i.precio, 0);
  const totalTickets = items.reduce((sum, i) => sum + i.tickets, 0);

  /* Restored from sessionStorage (login/signup return): keep it in history state so a reload
     doesn't lose the selected squares. */
  useEffect(() => {
    if (!location.state?.casillas?.length && casillas.length) {
      navigate(location.pathname, {
        replace: true,
        state: { casillas, rifaInfo, totalPrice, totalTickets, planByCasilla },
      });
    }
  }, []);

  useEffect(() => {
    if (rifaInfo?._id)
      trackAnalyticsEvent('checkout_start', {
        rifaId: rifaInfo._id,
        ticketCount: totalTickets,
        stage: 'details',
      });
  }, [rifaInfo, totalTickets]);

  const [{ loadingCreate }, dispatch] = useReducer(reducer, {
    loadingCreate: false,
  });

  const needed = totalTickets;
  // Bono with hidden numbers: the buyer never picks nor sees them (assigned server-side)
  const numerosOcultos = !mostrarNumerosOf(rifaInfo);

  /* ── Steps: account choice → plans (shared board) → numbers (if any) → buyer form ── */
  const steps = [
    !userInfo && 'account',
    shared && 'plans',
    needed > 0 && !numerosOcultos && 'numbers',
    'buyer',
  ].filter(Boolean);
  const [step, setStep] = useState(() => (userInfo ? (shared ? 'plans' : 'numbers') : 'account'));
  // A purchase without numbers (draws off / 0-number plans / hidden numbers) skips the number step
  const currentStep = step === 'numbers' && (needed === 0 || numerosOcultos) ? 'buyer' : step;
  const goNext = () => {
    const idx = steps.indexOf(currentStep);
    setStep(steps[Math.min(idx + 1, steps.length - 1)]);
  };

  /* ── Account mode ── */
  const [accountMode, setAccountMode] = useState(userInfo ? 'user' : null);

  // Number picker: rifa.pickerMode = 'manual' | 'random' | 'both' (toggle)
  const pickerMode = pickerModeOf(rifaInfo);
  const [numberMode, setNumberMode] = useState(pickerMode === 'random' ? 'random' : 'manual');
  const [allTickets, setAllTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [randomTickets, setRandomTickets] = useState([]);
  const [pickingRandom, setPickingRandom] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Buyer form – pre-fill from userInfo
  const [nombre, setNombre] = useState(userInfo?.nombre || '');
  const [email, setEmail] = useState(userInfo?.email || '');
  const [confirmEmail, setConfirmEmail] = useState(userInfo?.email || '');
  const [cedula, setCedula] = useState('');
  const [telefono, setTelefono] = useState(userInfo?.telefono || '');
  const [nombreVendedor, setNombreVendedor] = useState('');
  const {
    cuentas,
    selected: cuentaDestino,
    setSelected: setCuentaDestino,
    payload: cuentaPayload,
  } = useCuentasDestino();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const picked = selectedTicketIds.length;
  const remaining = needed - picked;

  // Plans changed → fewer numbers needed: drop the extra picks
  useEffect(() => {
    setSelectedTicketIds(prev => (prev.length > needed ? prev.slice(0, needed) : prev));
  }, [needed]);

  useEffect(() => {
    if (!casillas.length || !rifaInfo) navigate('/');
  }, [casillas.length, rifaInfo, navigate]);

  useEffect(() => {
    if (!rifaInfo?._id || pickerMode === 'random' || numerosOcultos) {
      setLoadingTickets(false);
      return;
    }
    (async () => {
      try {
        setLoadingTickets(true);
        const data = await ticketService.getAllByRifa(rifaInfo._id);
        setAllTickets(data);
      } catch {
        toast.error('No se pudieron cargar los números disponibles');
      } finally {
        setLoadingTickets(false);
      }
    })();
  }, [rifaInfo, pickerMode, numerosOcultos]);

  /** Random numbers for every square (server caps the count, validated again on reserve). */
  const pickRandomNumbers = async () => {
    if (!rifaInfo?._id || needed <= 0) return;
    try {
      setPickingRandom(true);
      const data = await ticketService.pickRandomAvailableTickets(rifaInfo._id, needed);
      const list = Array.isArray(data) ? data : [];
      setRandomTickets(list);
      setSelectedTicketIds(list.map(t => t._id));
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setPickingRandom(false);
    }
  };

  // Random mode: draw the numbers as soon as the step opens (or when the count changes)
  useEffect(() => {
    if (currentStep !== 'numbers' || numberMode !== 'random' || pickingRandom) return;
    if (selectedTicketIds.length !== needed) pickRandomNumbers();
  }, [currentStep, numberMode, needed]);

  const handleNumberModeChange = mode => {
    setNumberMode(mode);
    setSelectedTicketIds([]);
    setRandomTickets([]);
  };

  const toggleTicket = ticket => {
    if (ticket.estado !== 'Disponible') return;
    setSelectedTicketIds(prev => {
      if (prev.includes(ticket._id)) return prev.filter(id => id !== ticket._id);
      if (prev.length >= needed) return prev;
      return [...prev, ticket._id];
    });
  };

  const validateBuyerForm = () => {
    const errs = {};
    if (!nombre.trim()) errs.nombre = t('reservation.enterName');
    if (!email.trim()) errs.email = t('reservation.enterValidEmail');
    if (email !== confirmEmail) errs.confirmEmail = t('reservation.emailsMustMatch');
    if (!cedula.trim()) errs.cedula = t('reservation.enterDocument');
    if (!termsAccepted) errs.terms = t('terms.mustAccept') || 'Debés aceptar los términos';
    return errs;
  };

  const handleBuyerSubmit = async e => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validateBuyerForm();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (!numerosOcultos && picked < needed) {
      toast.error(
        t('reservation.mustPickAllNumbers', { needed, remaining }) ||
          `Seleccioná ${remaining} números más`
      );
      return;
    }

    try {
      dispatch({ type: 'CREATE_REQUEST' });
      const compradorPayload = {
        nombre: nombre.trim(),
        email: email.trim(),
        cedula: cedula.trim(),
      };
      if (telefono.trim()) compradorPayload.telefono = telefono.trim();

      const compradorData = await compradorService.createComprador(compradorPayload);
      const analytics = await getAnalyticsAttribution();
      const data = await reservaService.createReserva({
        comprador: compradorData,
        casillaIds: casillas.map(c => c._id),
        ...(shared ? { planByCasilla } : {}),
        // Hidden numbers: the server picks them at random (client ids would be ignored)
        chosenTicketIds: needed > 0 && !numerosOcultos ? selectedTicketIds : [],
        rifa: rifaInfo._id,
        fechaReserva: new Date(),
        importeReserva: totalPrice,
        analytics,
        ...cuentaPayload,
        ...(nombreVendedor.trim() ? { nombreVendedor: nombreVendedor.trim() } : {}),
      });
      if (!data?._id) throw new Error('Respuesta inválida del servidor');
      trackAnalyticsEvent('reservation_created', {
        rifaId: rifaInfo._id,
        ticketCount: totalTickets,
        stage: 'payment',
      });
      dispatch({ type: 'CREATE_SUCCESS' });
      navigate(paths.checkout(data._id), { replace: true });
    } catch (err) {
      dispatch({ type: 'CREATE_FAIL' });
      toast.error(getError(err));
    }
  };

  /* ── Navigate to sign-in / sign-up, saving casilla state ── */
  const goToAuth = authPath => {
    sessionStorage.setItem(
      'pendingCheckout',
      JSON.stringify({
        casillas,
        rifaInfo,
        totalPrice,
        totalTickets,
        planByCasilla,
        returnTo: paths.reserveBoard,
      })
    );
    navigate(`${authPath}?returnTo=${encodeURIComponent(paths.reserveBoard)}`);
  };

  const handleGuestMode = () => {
    setAccountMode('guest');
    goNext();
  };

  // Back button logic
  const handleBack = () => {
    const idx = steps.indexOf(currentStep);
    if (idx > 0) {
      const prev = steps[idx - 1];
      if (prev === 'account') setAccountMode(null);
      setStep(prev);
      return;
    }
    navigate(paths.buyBoard(rifaInfo._id), { replace: true });
  };

  // Group squares by category / chosen plan for the summary
  const byCategory = items.reduce((acc, i) => {
    const key = i.categoria || '—';
    if (!acc[key]) acc[key] = { qty: 0, price: i.precio, ticketsPerSq: i.tickets };
    acc[key].qty++;
    return acc;
  }, {});

  const availableTickets = allTickets.filter(
    t =>
      t.estado === 'Disponible' &&
      (searchQuery === '' || t.nroSerial.toString().includes(searchQuery))
  );

  if (!casillas.length || !rifaInfo) return null;

  /* ── Stepper step mapping ── */
  const stepperStep =
    currentStep === 'account' || currentStep === 'plans' ? 1 : currentStep === 'numbers' ? 2 : 3;

  /* ── Action button ── */
  const actionButton = (extraClass = '') =>
    currentStep === 'plans' ? (
      <button
        type='button'
        onClick={goNext}
        className={`flex w-full items-center justify-center rounded-xl bg-ink px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-40 ${extraClass}`}
      >
        {t('common.continue', 'Continuar')} →
      </button>
    ) : currentStep === 'numbers' ? (
      <button
        type='button'
        disabled={remaining > 0 || pickingRandom}
        onClick={goNext}
        className={`flex w-full items-center justify-center rounded-xl bg-ink px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-40 ${extraClass}`}
      >
        {remaining > 0
          ? `Seleccioná ${remaining} número${remaining !== 1 ? 's' : ''} más`
          : 'Continuar →'}
      </button>
    ) : (
      <button
        type='submit'
        form='buyer-form'
        disabled={loadingCreate}
        className={`flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-50 ${extraClass}`}
      >
        {loadingCreate ? (
          <>
            <Loader2 className='h-4 w-4 animate-spin' />
            Procesando…
          </>
        ) : (
          'Continuar al pago →'
        )}
      </button>
    );

  const topTitle =
    currentStep === 'account'
      ? t('checkout.title', 'Finalizar compra')
      : currentStep === 'plans'
        ? t('board.planStepTitle', 'Elegí tus planes')
        : currentStep === 'numbers'
          ? 'Elegí tus números'
          : 'Tus datos';

  const selectedTickets = [...allTickets, ...randomTickets].filter(
    (tk, i, arr) => selectedTicketIds.includes(tk._id) && arr.findIndex(x => x._id === tk._id) === i
  );

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      {/* ─── Top bar ─────────────────────────────────────────── */}
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <button
          onClick={handleBack}
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface'
          aria-label='Volver'
        >
          <ArrowLeft className='h-4 w-4' />
        </button>
        <span className='text-base font-semibold lg:text-lg'>{topTitle}</span>
      </div>

      {/* ─── Main content ────────────────────────────────────── */}
      <div className='mx-auto max-w-7xl px-4 pb-28 lg:px-8 lg:pb-12'>
        <div className='mb-6'>
          <CheckoutStepper currentStep={stepperStep} />
        </div>

        <div className='lg:grid lg:grid-cols-5 lg:items-start lg:gap-10'>
          {/* ── Left column: summary + desktop action ── */}
          <div className='lg:col-span-2 lg:sticky lg:top-24 space-y-4'>
            <div className='rounded-xl bg-surface px-4 py-3 space-y-1.5'>
              <p className='text-sm font-semibold'>{rifaInfo.nombre}</p>
              {Object.entries(byCategory).map(([cat, { qty, price, ticketsPerSq }]) => (
                <div key={cat} className='flex justify-between text-sm'>
                  <span>
                    <span className='font-medium'>{cat}</span>
                    <span className='text-zinc-400 ml-1'>× {qty}</span>
                    {ticketsPerSq > 0 && (
                      <span className='text-zinc-400 ml-1'>({ticketsPerSq * qty} núm.)</span>
                    )}
                  </span>
                  <span className='font-semibold'>${price * qty}</span>
                </div>
              ))}
              <div className='flex justify-between text-sm font-bold border-t border-divider pt-1.5'>
                <span>Total</span>
                <span>${totalPrice}</span>
              </div>
            </div>

            {currentStep !== 'account' && <div className='hidden lg:block'>{actionButton()}</div>}
          </div>

          {/* ── Right column ── */}
          <div className='lg:col-span-3 mt-5 lg:mt-0 space-y-4'>
            {/* ══ STEP 0: ACCOUNT CHOICE ══ */}
            {currentStep === 'account' && (
              <AccountChoice
                t={t}
                onGuest={handleGuestMode}
                onSignIn={() => goToAuth(paths.signIn)}
                onSignUp={() => goToAuth(paths.signUp)}
              />
            )}

            {/* ══ STEP: PLAN PER SQUARE (shared board) ══ */}
            {currentStep === 'plans' && (
              <div className='space-y-3'>
                <p className='text-sm font-semibold'>
                  {t('board.choosePlans', 'Elegí un plan para cada casilla')}
                </p>
                {casillas.length > 1 && plans.length > 1 && (
                  <div className='flex items-center gap-2 text-sm'>
                    <span className='text-zinc-500'>
                      {t('board.applyToAll', 'Aplicar a todas')}:
                    </span>
                    <select
                      className='rounded-xl border border-divider bg-white px-3 py-2 text-sm'
                      value=''
                      onChange={e => {
                        const value = e.target.value;
                        if (!value) return;
                        setPlanByCasilla(Object.fromEntries(casillas.map(c => [c._id, value])));
                      }}
                    >
                      <option value=''>—</option>
                      {plans.map(p => (
                        <option key={p.nombre} value={p.nombre}>
                          {p.nombre} — ${p.precioCasilla}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {items.map(({ casilla, color, precio, tickets }, idx) => (
                  <div
                    key={casilla._id}
                    className='flex flex-wrap items-center gap-3 rounded-xl border border-divider bg-surface px-3 py-2.5'
                  >
                    <span
                      className='h-4 w-4 shrink-0 rounded'
                      style={{ backgroundColor: color || '#888' }}
                    />
                    <div className='min-w-[7rem] flex-1'>
                      <p className='text-sm font-semibold'>
                        {t('board.squareN', { n: idx + 1, defaultValue: 'Casilla {{n}}' })}
                      </p>
                      <p className='text-xs text-zinc-500'>
                        {t('board.squarePosition', {
                          fila: Number(casilla.fila) + 1,
                          columna: Number(casilla.columna) + 1,
                          defaultValue: 'Fila {{fila}} · Columna {{columna}}',
                        })}
                      </p>
                    </div>
                    <select
                      className='min-w-[10rem] flex-1 rounded-xl border border-divider bg-white px-3 py-2 text-sm'
                      value={planByCasilla[casilla._id] || ''}
                      onChange={e =>
                        setPlanByCasilla(prev => ({ ...prev, [casilla._id]: e.target.value }))
                      }
                    >
                      {plans.map(p => (
                        <option key={p.nombre} value={p.nombre}>
                          {p.nombre} — ${p.precioCasilla}
                        </option>
                      ))}
                    </select>
                    <div className='w-full text-right text-sm sm:w-auto'>
                      <span className='font-semibold'>${precio}</span>
                      {tickets > 0 && (
                        <span className='ml-1 text-xs text-zinc-400'>
                          ·{' '}
                          {t('board.numbersIncluded', {
                            count: tickets,
                            defaultValue: '{{count}} números incluidos',
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ══ STEP: NUMBER PICKER (manual grid and/or random, per rifa.pickerMode) ══ */}
            {currentStep === 'numbers' && (
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <p className='text-sm font-semibold'>Seleccioná {needed} números</p>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      remaining === 0 ? 'bg-green-100 text-green-700' : 'bg-surface text-zinc-600'
                    }`}
                  >
                    {picked}/{needed}
                  </span>
                </div>

                {pickerMode === 'both' && (
                  <div className='flex gap-2'>
                    {['manual', 'random'].map(mode => (
                      <button
                        key={mode}
                        type='button'
                        onClick={() => mode !== numberMode && handleNumberModeChange(mode)}
                        className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                          numberMode === mode
                            ? 'border-ink bg-ink text-white'
                            : 'border-divider bg-surface text-ink hover:border-ink'
                        }`}
                      >
                        {mode === 'manual'
                          ? t('ticket.selectionModeManual', 'Elegir manualmente')
                          : t('ticket.selectionModeRandom', 'Números al azar')}
                      </button>
                    ))}
                  </div>
                )}

                {numberMode === 'random' ? (
                  <div className='space-y-3'>
                    <p className='text-xs text-zinc-500'>
                      {t(
                        'reservation.randomNumbersAssigned',
                        'Te asignamos estos números al azar.'
                      )}
                    </p>
                    {pickingRandom ? (
                      <div className='flex items-center gap-2 py-4 text-sm text-zinc-500'>
                        <Loader2 className='h-5 w-5 animate-spin' />
                        {t('reservation.pickingRandom', 'Sorteando números…')}
                      </div>
                    ) : (
                      <div className='flex flex-wrap gap-1.5'>
                        {selectedTickets.map(ticket => (
                          <span
                            key={ticket._id}
                            className='flex h-9 min-w-[3.5rem] items-center justify-center rounded-lg bg-ink px-2 text-sm font-semibold text-white'
                          >
                            {ticket.nroSerial}
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      type='button'
                      disabled={pickingRandom}
                      onClick={pickRandomNumbers}
                      className='inline-flex items-center gap-2 rounded-xl border border-divider bg-surface px-3 py-2 text-sm font-semibold hover:border-ink disabled:opacity-50'
                    >
                      <RefreshCw className='h-4 w-4' />
                      {t('reservation.reshuffle', 'Volver a sortear')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className='relative'>
                      <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400' />
                      <input
                        type='number'
                        placeholder='Buscar número…'
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className='w-full rounded-xl border border-divider bg-surface py-2.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-ink/20'
                      />
                    </div>

                    {picked > 0 && (
                      <div className='flex flex-wrap gap-1.5'>
                        {selectedTickets.map(ticket => (
                          <button
                            key={ticket._id}
                            type='button'
                            onClick={() => toggleTicket(ticket)}
                            className='flex h-9 w-14 items-center justify-center rounded-lg bg-ink text-sm font-semibold text-white'
                          >
                            {ticket.nroSerial}
                          </button>
                        ))}
                      </div>
                    )}

                    {loadingTickets ? (
                      <div className='flex justify-center py-6'>
                        <Loader2 className='h-6 w-6 animate-spin text-zinc-400' />
                      </div>
                    ) : (
                      <div className='grid grid-cols-6 gap-1.5 sm:grid-cols-8 lg:grid-cols-8 xl:grid-cols-10'>
                        {availableTickets.map(ticket => {
                          const sel = selectedTicketIds.includes(ticket._id);
                          const full = !sel && picked >= needed;
                          return (
                            <button
                              key={ticket._id}
                              type='button'
                              disabled={full}
                              onClick={() => toggleTicket(ticket)}
                              className={`h-9 rounded-lg text-sm font-semibold transition-colors ${
                                sel
                                  ? 'bg-ink text-white'
                                  : full
                                    ? 'border border-divider bg-surface text-zinc-300'
                                    : 'border border-divider bg-surface text-ink hover:border-ink'
                              }`}
                            >
                              {ticket.nroSerial}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ══ STEP: BUYER FORM ══ */}
            {currentStep === 'buyer' && (
              <form id='buyer-form' onSubmit={handleBuyerSubmit} noValidate className='space-y-4'>
                <Field label='Nombre completo' error={submitted ? errors.nombre : undefined}>
                  <InputField
                    type='text'
                    placeholder='Juan García'
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                  />
                </Field>

                <Field label='Correo electrónico' error={submitted ? errors.email : undefined}>
                  <InputField
                    type='email'
                    placeholder='juan@ejemplo.com'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </Field>

                <Field
                  label='Confirmar correo electrónico'
                  error={submitted ? errors.confirmEmail : undefined}
                >
                  <InputField
                    type='email'
                    placeholder='juan@ejemplo.com'
                    value={confirmEmail}
                    onChange={e => setConfirmEmail(e.target.value)}
                  />
                </Field>

                <Field label='Teléfono (opcional)'>
                  <InputField
                    type='tel'
                    placeholder='+598 9X XXX XXX'
                    value={telefono}
                    onChange={e => setTelefono(e.target.value)}
                  />
                </Field>

                <Field
                  label={t('reservation.documentLabel', 'Documento de identidad')}
                  error={submitted ? errors.cedula : undefined}
                >
                  <InputField
                    type='text'
                    placeholder={t('reservation.documentPlaceholder', 'Pasaporte, DNI, cédula…')}
                    value={cedula}
                    onChange={e => setCedula(e.target.value)}
                    required
                  />
                </Field>

                <Field label='Vendedor (opcional)'>
                  <InputField
                    type='text'
                    placeholder='Nombre del vendedor'
                    value={nombreVendedor}
                    onChange={e => setNombreVendedor(e.target.value)}
                  />
                </Field>

                <CuentaDestinoField
                  t={t}
                  cuentas={cuentas}
                  value={cuentaDestino}
                  onChange={setCuentaDestino}
                />

                <div className='flex items-start gap-3'>
                  <input
                    id='terms'
                    type='checkbox'
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    className='mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-ink'
                  />
                  <label htmlFor='terms' className='text-sm leading-snug'>
                    {t('terms.acceptTerms')}{' '}
                    <Link to='/bases' target='_blank' className='text-brand underline'>
                      {t('terms.viewTerms')}
                    </Link>
                  </label>
                </div>
                {submitted && errors.terms && (
                  <p className='text-xs text-red-500'>{errors.terms}</p>
                )}

                <div className='flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2.5'>
                  <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-yellow-600' />
                  <p className='text-xs text-yellow-800'>{t('reservation.verifyDataWarning')}</p>
                </div>

                {accountMode === 'user' && (
                  <p className='text-xs text-zinc-400 text-center'>
                    {t('checkout.notYou', '¿No sos vos?')}{' '}
                    <button
                      type='button'
                      onClick={() => {
                        setAccountMode('guest');
                        setNombre('');
                        setEmail('');
                        setConfirmEmail('');
                        setTelefono('');
                      }}
                      className='underline text-zinc-500 hover:text-ink'
                    >
                      {t('checkout.continueAsGuest', 'Continuar como invitado')}
                    </button>
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ─── Mobile sticky bar ─────────────────────────────── */}
      {currentStep !== 'account' && (
        <div className='fixed bottom-0 left-0 right-0 z-30 border-t border-divider bg-white px-4 py-3 lg:hidden'>
          {actionButton()}
        </div>
      )}
    </div>
  );
}
